import type { KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import {
  Dialog,
  type OnRequestClose,
  RouterProvider,
  theme,
  useActiveWindowBreakpoint,
  useIsAppleOs,
  useOnStateChange,
} from '@frameio/vapor';
import { useIsAdvancedSearchEnabled } from '../../../advanced-search/hooks';
import {
  useQuickSearchModal,
  useTrackSearchExited,
} from '../../../shared/contexts';
import { useNavigateToAdvancedSearch } from '../../hooks';
import { QuickSearchCompactContent } from '../QuickSearchCompact';
import { QuickSearchDialogContent } from './QuickSearchDialogContent';

/**
 * Renders the Quick Search dialog.
 *
 * This component is responsible for the presentation and lifecycle of the quick
 * search modal. It handles opening and closing the dialog, navigation to a
 * selected result, and adapts its size for mobile and desktop viewports.
 */
export const QuickSearchDialog = () => {
  const router = useRouter();
  const initialFocusInputRef = useRef<HTMLInputElement>(null);
  const breakpoint = useActiveWindowBreakpoint();
  const trackSearchExited = useTrackSearchExited();
  const { isModalOpen, closeModal, query } = useQuickSearchModal();
  const [isNavigateToResultPending, startNavigateToResult] = useTransition();
  // Captured at navigation start and passed to closeModal when the transition resolves
  const navigatingToRef = useRef<string | null>(null);

  const handleNavigateToResult = useCallback(
    (href: string) => {
      navigatingToRef.current = href;
      startNavigateToResult(() => router.push(href));
    },
    [startNavigateToResult, router]
  );

  const handleOnRequestClose: OnRequestClose = useCallback(
    (evt) => {
      trackSearchExited({ event: evt });
      closeModal({ clearState: true });
    },
    [closeModal, trackSearchExited]
  );

  useOnStateChange(isNavigateToResultPending, () => {
    if (!isNavigateToResultPending && isModalOpen) {
      closeModal({ navigatingTo: navigatingToRef.current ?? undefined });
      navigatingToRef.current = null;
    }
  });

  const dialogSizeProps = useMemo(
    () =>
      breakpoint === 'base'
        ? {
            size: 'fullscreen' as const,
            width: undefined as never,
          }
        : {
            position: 'top' as const,
            size: undefined as never,
            width: 'fit-content' as const,
          },
    [breakpoint]
  );

  const isAppleOs = useIsAppleOs();
  const isAdvancedSearchEnabled = useIsAdvancedSearchEnabled();
  const navigateToAdvancedSearch = useNavigateToAdvancedSearch();
  const handleOnKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isAdvancedSearchEnabled || !query) return;

      const modKey = isAppleOs ? event.metaKey : event.ctrlKey;
      if (modKey && event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        navigateToAdvancedSearch({ event });
      }
    },
    [isAppleOs, isAdvancedSearchEnabled, navigateToAdvancedSearch, query]
  );

  return (
    <Dialog
      data-testid="quick-search-modal-alt"
      hasOutermostTheme={true}
      initialFocusRef={initialFocusInputRef}
      isShowing={isModalOpen}
      onRequestClose={handleOnRequestClose}
      {...dialogSizeProps}
      onKeyDownCapture={handleOnKeyDownCapture}
      style={{
        // use 3D transform to avoid layout shift
        backfaceVisibility: 'hidden',
        background: 'transparent', // remove to reduce perceived layout shift since dialog content has a background
        border: 'none',
        perspective: 1000,
        transformOrigin: 'bottom',
        transformStyle: 'preserve-3d',
        transitionDuration: theme.transitions.durations[250],
        transitionProperty: 'transform, opacity, width, max-width',
        transitionTimingFunction: theme.transitions.eases.fastStart,
        transitionBehavior: 'allow-discrete',
        willChange: 'transform, opacity',
      }}
    >
      <RouterProvider navigate={handleNavigateToResult}>
        {isAdvancedSearchEnabled ? (
          <QuickSearchCompactContent
            initialFocusInputRef={initialFocusInputRef}
            isMobile={breakpoint === 'base'}
            onDialogClose={handleOnRequestClose}
          />
        ) : (
          <QuickSearchDialogContent
            ref={initialFocusInputRef}
            isMobile={breakpoint === 'base'}
            onDialogClose={handleOnRequestClose}
          />
        )}
      </RouterProvider>
    </Dialog>
  );
};
